import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  type BuildJobDto,
  type CandidateRowDto,
  type ModelConfigDto,
  type SegmentOptionDto,
  declareTarget,
  deleteModelConfig,
  fetchBuildJob,
  fetchBuilderSegments,
  fetchLabCandidates,
  fetchOfferedFeatures,
  listModelConfigs,
  lockHorizon,
  persistModel,
  runBakeoff,
  runPrune,
  saveModelConfig,
  submitBuild,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";

const HORIZON_FAMILY_LABELS: Record<string, string> = {
  intraday: "Intraday (~1 day)",
  short: "Short (5–30 d)",
  mid: "Mid (1–3 mo)",
  long: "Long (3–12 mo)",
};
const N_CLASS_LABELS: Record<number, string> = {
  2: "2 · up/down",
  3: "3 · sell/hold/buy",
  5: "5 · strong-sell…strong-buy",
};

function StepHeader({ n, title, sub, done, active, badge }: {
  n: number; title: string; sub: string; done: boolean; active: boolean; badge?: string;
}) {
  return (
    <div className="h3" style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{
        width: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center",
        fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.8rem", flex: "none",
        background: done ? "var(--ok)" : active ? "var(--ink)" : "var(--surface-2)",
        color: done || active ? "#fff" : "var(--ink-faint)",
        border: "1px solid var(--rule)",
      }}>{n}</span>
      <span>
        {title} <span className="note">{sub}</span>
      </span>
      {badge && <span className="chip st-champion" style={{ marginLeft: "auto" }}>{badge}</span>}
    </div>
  );
}

export function ModelBuilderPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [error, setError] = useState<string | null>(null);
  function authAware(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) { onUnauthorized(); return true; }
    return false;
  }

  // Step 0 -- declare target
  const [targetType, setTargetType] = useState<"return" | "volatility" | "direction">("return");
  const [level, setLevel] = useState<"stock" | "index">("stock");
  const [segments, setSegments] = useState<SegmentOptionDto[] | null>(null);
  const [targetRef, setTargetRef] = useState("");
  const [nClasses, setNClasses] = useState(3);
  const [predictionType, setPredictionType] = useState<"regression" | "classification" | null>(null);
  const [families, setFamilies] = useState<Record<string, number[]> | null>(null);
  const [targetConfirmed, setTargetConfirmed] = useState(false);

  useEffect(() => {
    fetchBuilderSegments(token).then(setSegments).catch((e) => { if (!authAware(e)) setError(String(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Saved build recipes (data_spec S9.4 -- target/level/segment/n_classes
  // only: the wizard's OWN starting inputs, not a trained model or a
  // skipped gate. Loading one just pre-fills step 0.
  const [savedConfigs, setSavedConfigs] = useState<ModelConfigDto[] | null>(null);
  const [configName, setConfigName] = useState("");
  const [configNote, setConfigNote] = useState<string | null>(null);

  function refreshConfigs() {
    listModelConfigs(token).then(setSavedConfigs).catch(() => undefined);
  }
  useEffect(() => { refreshConfigs(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveCurrentConfig() {
    if (!configName.trim()) return;
    try {
      await saveModelConfig(token, configName.trim(), {
        target_type: targetType, level, target_ref: targetRef,
        n_classes: targetType === "direction" ? nClasses : undefined,
      });
      setConfigName("");
      setConfigNote("Saved.");
      refreshConfigs();
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "save config failed");
    }
  }

  function loadConfig(cfg: ModelConfigDto) {
    const s = cfg.setup as { target_type?: string; level?: string; target_ref?: string; n_classes?: number };
    if (s.target_type) setTargetType(s.target_type as typeof targetType);
    if (s.level) setLevel(s.level as typeof level);
    if (s.target_ref) setTargetRef(s.target_ref);
    if (s.n_classes) setNClasses(s.n_classes);
    setConfigNote(`Loaded "${cfg.name}" — review the fields below, then Confirm target.`);
  }

  async function removeConfig(configId: string) {
    try {
      await deleteModelConfig(token, configId);
      refreshConfigs();
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "delete config failed");
    }
  }

  useEffect(() => {
    if (level === "stock" && segments && segments.length && !targetRef) setTargetRef(segments[0].segment_key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, segments]);

  async function confirmTarget() {
    try {
      const res = await declareTarget(token, {
        target_type: targetType, level, target_ref: targetRef,
        n_classes: targetType === "direction" ? nClasses : null,
      });
      setPredictionType(res.prediction_type);
      setFamilies(res.families);
      setTargetConfirmed(true);
      setHorizonFamily(Object.keys(res.families)[1] ?? Object.keys(res.families)[0]);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "target declaration failed");
    }
  }

  // Step 1 -- horizon
  const [horizonFamily, setHorizonFamily] = useState("short");
  const [job, setJob] = useState<BuildJobDto | null>(null);
  const [chosenHorizon, setChosenHorizon] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function stopPoll() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }
  function startPoll(jobId: string) {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const j = await fetchBuildJob(token, jobId);
        setJob(j);
        if (j.status !== "running") stopPoll();
      } catch (e) {
        stopPoll();
        if (!authAware(e)) setError(e instanceof ApiError ? e.message : "poll failed");
      }
    }, 3000);
  }
  useEffect(() => () => stopPoll(), []);

  async function startHorizonSearch() {
    try {
      const j = await submitBuild(token, {
        target_type: targetType, level, target_ref: targetRef,
        n_classes: targetType === "direction" ? nClasses : null,
        horizon_family: horizonFamily,
      });
      setJob(j);
      if (j.status === "running") startPoll(j.job_id);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "could not start build");
    }
  }

  useEffect(() => {
    if (job?.horizon_result?.locked !== null && job?.horizon_result?.locked !== undefined && chosenHorizon === null) {
      setChosenHorizon(job.horizon_result.locked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.horizon_result]);

  async function confirmLockHorizon() {
    if (!job || chosenHorizon === null) return;
    try {
      const j = await lockHorizon(token, job.job_id, chosenHorizon);
      setJob(j);
      const feats = await fetchOfferedFeatures(token, job.job_id);
      setOffered(feats);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "lock failed");
    }
  }

  // Step 2 -- select vars
  const [offered, setOffered] = useState<string[] | null>(null);
  const [candidates, setCandidates] = useState<CandidateRowDto[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    fetchLabCandidates(token).then(setCandidates).catch(() => undefined);
  }, [token]);
  useEffect(() => {
    if (offered && selected.size === 0) setSelected(new Set(offered));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offered]);
  const candByKey = useMemo(() => {
    const m = new Map<string, CandidateRowDto>();
    (candidates ?? []).forEach((c) => m.set(c.feature_key, c));
    return m;
  }, [candidates]);

  function toggleSelected(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  async function confirmVars() {
    if (!job) return;
    try {
      const j = await runPrune(token, job.job_id, Array.from(selected), []);
      setJob(j);
      if (j.status === "running") startPoll(j.job_id);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "prune failed");
    }
  }

  // Step 3 -- prune loop
  async function rerunPrune() {
    if (!job || !job.prune_result) return;
    const nextExcluded = new Set(excluded);
    job.prune_result.selected.filter((k) => !selected.has(k)).forEach((k) => nextExcluded.add(k));
    setExcluded(nextExcluded);
    try {
      const j = await runPrune(token, job.job_id, job.prune_result.offered, Array.from(nextExcluded));
      setJob(j);
      if (j.status === "running") startPoll(j.job_id);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "prune failed");
    }
  }
  function toggleKeepSelected(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function confirmImportance() {
    if (!job) return;
    try {
      const j = await runBakeoff(token, job.job_id);
      setJob(j);
      if (j.status === "running") startPoll(j.job_id);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "bake-off failed");
    }
  }

  // Step 4 -- bake-off
  const [chosenFamily, setChosenFamily] = useState<string | null>(null);
  useEffect(() => {
    if (job?.bakeoff_result && !chosenFamily) setChosenFamily(job.bakeoff_result.winner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.bakeoff_result]);

  // Step 5 -- accept & persist
  const [modelId, setModelId] = useState("");
  useEffect(() => {
    if (job?.bakeoff_result && chosenFamily && !modelId) {
      const seg = targetRef.replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase();
      setModelId(`${seg}_${targetType}_h${chosenHorizon}_${chosenFamily}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.bakeoff_result, chosenFamily]);

  async function acceptAndPersist() {
    if (!job) return;
    try {
      const j = await persistModel(token, job.job_id, modelId, chosenFamily);
      setJob(j);
      if (j.status === "running") startPoll(j.job_id);
    } catch (e) {
      if (!authAware(e)) setError(e instanceof ApiError ? e.message : "persist failed");
    }
  }

  const s0done = targetConfirmed;
  const s1done = !!(job && job.spec.horizon);
  const s3done = !!(job && job.prune_result);
  const s4done = !!(job && job.bakeoff_result);
  const s5done = !!(job && job.persist_result);
  const refused = job?.status === "refused";
  const jobError = job?.status === "error";

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Model Builder</h1>
          <div className="page-sub">
            Build one cross-sectional predictor — segment target, optimized horizon, compared families, persisted winner.
          </div>
        </div>
      </div>

      <PageHelper>
        Every step below routes through the SAME gated pipeline the batch-built production models went through — the
        horizon must genuinely lock (<FieldHelp term="horizon_shape" />a real plateau, not a spike or a flat curve),
        feature selection runs the real <FieldHelp term="permutation_null" />permutation-derived importance cut and{" "}
        <FieldHelp term="vif" />VIF prune, and the bake-off compares ≥4 real model families on real{" "}
        <FieldHelp term="walk_forward" />walk-forward folds. If any stage refuses, nothing persists — the same honest
        refusal a batch build would show, not a friendlier UI shortcut. Builds are genuinely slow (minutes to over an
        hour depending on segment size) — this runs as a background job you can watch progress on, not a single
        request.
      </PageHelper>

      {error && <div className="error" style={{ margin: "10px 0" }}>{error}</div>}

      {/* STEP 0 */}
      <div className="card">
        <StepHeader n={0} title="Declare the target" sub="stocks are modelled by segment, not one-by-one" done={s0done} active={!s0done} />
        <div style={{ padding: 16 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <label className="note" style={{ display: "block", marginBottom: 4 }}>Target type</label>
              <select value={targetType} disabled={targetConfirmed} onChange={(e) => setTargetType(e.target.value as typeof targetType)}>
                <option value="return">return</option>
                <option value="volatility">volatility</option>
                <option value="direction">direction</option>
              </select>
            </div>
            <div>
              <label className="note" style={{ display: "block", marginBottom: 4 }}>Level</label>
              <select value={level} disabled={targetConfirmed} onChange={(e) => { setLevel(e.target.value as typeof level); setTargetRef(""); }}>
                <option value="stock">Stock (cross-sectional segment)</option>
                <option value="index" disabled title="Closed research finding, not a missing feature: index μ resolved to neutral drift (0/42 blessed features cleared significance at h=5 or h=70) and index σ already has a validated, parameter-free persistence convention that beat two different fitted attempts in its own pilot. Fitting a new index model here would re-run a question this project already answered negatively.">
                  Index (per-instrument) — not offered
                </option>
              </select>
              <div className="hint" style={{ fontSize: "0.7rem", color: "var(--ink-faint)", fontStyle: "italic", marginTop: 3, maxWidth: 220 }}>
                Indices aren't offered here — μ is neutral drift and σ is a validated persistence convention (see the Index Track finding), so there's no fitted-model gap to fill.
              </div>
            </div>
            <div>
              <label className="note" style={{ display: "block", marginBottom: 4 }}>Segment</label>
              <select value={targetRef} disabled={targetConfirmed} onChange={(e) => setTargetRef(e.target.value)} style={{ minWidth: 240 }}>
                {(segments ?? []).map((s) => (
                  <option key={s.segment_key} value={s.segment_key}>
                    {s.segment_key} ({s.n_constituents})
                  </option>
                ))}
              </select>
            </div>
            {targetType === "direction" && (
              <div>
                <label className="note" style={{ display: "block", marginBottom: 4 }}>Classes</label>
                <select value={nClasses} disabled={targetConfirmed} onChange={(e) => setNClasses(Number(e.target.value))}>
                  {[2, 3, 5].map((n) => <option key={n} value={n}>{N_CLASS_LABELS[n]}</option>)}
                </select>
              </div>
            )}
            {!targetConfirmed && (
              <button className="btn primary sm" onClick={confirmTarget} disabled={!targetRef}>Confirm target →</button>
            )}
          </div>

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--rule)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span className="note">Saved recipes</span>
            {(savedConfigs ?? []).map((cfg) => (
              <span key={cfg.config_id} className="chip" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ cursor: targetConfirmed ? "not-allowed" : "pointer" }} onClick={() => !targetConfirmed && loadConfig(cfg)}>{cfg.name}</span>
                <span style={{ cursor: "pointer", color: "var(--ink-faint)" }} onClick={() => removeConfig(cfg.config_id)} title="Delete this saved recipe">✕</span>
              </span>
            ))}
            {savedConfigs && savedConfigs.length === 0 && <span className="note" style={{ fontStyle: "italic" }}>none yet</span>}
            {!targetConfirmed && (
              <>
                <input className="txt" style={{ width: 160 }} placeholder="name this recipe…" value={configName} onChange={(e) => setConfigName(e.target.value)} />
                <button className="btn ghost sm" onClick={saveCurrentConfig} disabled={!configName.trim() || !targetRef}>💾 Save recipe</button>
              </>
            )}
            {configNote && <span className="note" style={{ color: "var(--ok)" }}>{configNote}</span>}
          </div>

          {targetConfirmed && predictionType && (
            <div className="banner show" style={{ display: "flex", marginTop: 12 }}>
              <span>◆</span>
              <span>
                Prediction type: <b className="mono">{predictionType}</b> <FieldHelp term="prediction_type_derived" />
                {" "}— derived from the target, not chosen. {level === "stock"
                  ? `Cross-sectional model: one model trained on all ${segments?.find((s) => s.segment_key === targetRef)?.n_constituents ?? "?"} constituents of "${targetRef}" pooled — the stock isn't the target, it's a row.`
                  : "Per-instrument model — indices are few and behave idiosyncratically, so each gets its own model."}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* STEP 1 */}
      <div className="card" style={{ opacity: s0done ? 1 : 0.5 }}>
        <StepHeader n={1} title="Horizon — pick a family, optimize within it" sub="the optimizer finds the horizon and shows if it's robust"
          done={s1done} active={s0done && !s1done}
          badge={s1done ? `${job?.spec.horizon}d${job?.spec.horizon_overridden ? " (override)" : ""}` : undefined} />
        {!s0done ? (
          <div className="locked" style={{ padding: 16, color: "var(--ink-faint)", fontStyle: "italic" }}>Confirm the target first.</div>
        ) : (
          <div style={{ padding: 16 }}>
            {!job && (
              <>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {Object.keys(families ?? {}).map((k) => (
                    <button key={k} className={`fam-btn${horizonFamily === k ? " on" : ""}`}
                      style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "0.82rem", border: "1px solid var(--rule-strong)", borderRadius: 8, padding: "9px 14px", cursor: "pointer", background: horizonFamily === k ? "var(--accent-soft)" : "#fff", color: horizonFamily === k ? "var(--accent)" : "var(--ink)" }}
                      onClick={() => setHorizonFamily(k)}>
                      {HORIZON_FAMILY_LABELS[k] ?? k}
                      <div className="mono" style={{ fontSize: "0.68rem", marginTop: 2 }}>{(families?.[k] ?? []).map((h) => `${h}d`).join(", ")}</div>
                    </button>
                  ))}
                </div>
                <button className="btn primary sm" onClick={startHorizonSearch}>▶ Optimize horizon within family</button>
                <div className="hint" style={{ fontSize: "0.74rem", color: "var(--ink-faint)", fontStyle: "italic", marginTop: 8 }}>
                  This builds a real panel at EACH candidate horizon in the family — measured minutes even on the
                  smallest segment (see the page helper above). Progress streams below once started.
                </div>
              </>
            )}
            {job && job.status === "running" && (
              <div>
                <div className="note" style={{ marginBottom: 6 }}>{job.stage.replace(/_/g, " ")}…</div>
                <BuildLog log={job.log} />
              </div>
            )}
            {job && job.status === "refused" && !job.horizon_result?.locked && job.stage.startsWith("horizon") && (
              <>
                <BuildLog log={job.log} />
                <div className="banner show" style={{ display: "flex", marginTop: 10, background: "var(--down-soft)" }}>
                  <span>⛔</span>
                  <span>
                    <b>REFUSED at the horizon gate — {job.horizon_result?.shape}.</b> {job.horizon_result?.reason}
                    <FieldHelp term="horizon_shape" /> Nothing downstream can proceed: F5's discipline is enforced
                    here exactly as the batch builder enforces it, not relaxed for this UI.
                  </span>
                </div>
              </>
            )}
            {job && job.horizon_result?.locked !== null && job.horizon_result?.locked !== undefined && !s1done && (
              <>
                <BuildLog log={job.log} />
                <HorizonCurve result={job.horizon_result} />
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
                  <label className="note">Lock at:</label>
                  <select value={chosenHorizon ?? job.horizon_result.locked} onChange={(e) => setChosenHorizon(Number(e.target.value))}>
                    {Object.keys(job.horizon_result.curve).filter((h) => job.horizon_result!.curve[h] !== null).map((h) => (
                      <option key={h} value={h}>{h}d{Number(h) === job.horizon_result!.locked ? " (proposed)" : ""}</option>
                    ))}
                  </select>
                  <button className="btn primary sm" onClick={confirmLockHorizon}>Lock horizon →</button>
                </div>
              </>
            )}
            {s1done && <BuildLog log={job!.log} collapsedByDefault />}
          </div>
        )}
      </div>

      {/* STEP 2 */}
      <div className="card" style={{ opacity: s1done ? 1 : 0.5 }}>
        <StepHeader n={2} title="Select independent variables" sub="multiple, from the blessed library — evaluated at the locked horizon"
          done={s3done} active={s1done && !s3done} badge={s3done ? `${job?.prune_result?.selected.length} vars` : undefined} />
        {!s1done ? (
          <div className="locked" style={{ padding: 16, color: "var(--ink-faint)", fontStyle: "italic" }}>Lock the horizon first.</div>
        ) : !offered ? (
          <div className="locked" style={{ padding: 16, color: "var(--ink-faint)", fontStyle: "italic" }}>Loading the blessed library…</div>
        ) : job?.status === "running" && job.stage === "pruning" ? (
          <div style={{ padding: 16 }}><BuildLog log={job.log} /></div>
        ) : !s3done ? (
          <div style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {offered.map((key) => {
                const c = candByKey.get(key);
                const on = selected.has(key);
                return (
                  <div key={key} onClick={() => toggleSelected(key)}
                    style={{ border: `1px solid ${on ? "var(--accent)" : "var(--rule)"}`, background: on ? "var(--accent-soft)" : "#fff", borderRadius: 8, padding: "10px 12px", cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "0.85rem" }}>
                      <span>{key}</span><span style={{ color: on ? "var(--accent)" : "var(--ink-faint)" }}>{on ? "✓" : "+"}</span>
                    </div>
                    <div className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)", display: "flex", gap: 8, marginTop: 3 }}>
                      {c?.ic !== null && c?.ic !== undefined && <span>IC {c.ic >= 0 ? "+" : ""}{c.ic.toFixed(3)} <FieldHelp term="ic" /></span>}
                      {c?.stability && <span>{c.stability}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 14 }}>
              <button className="btn primary sm" onClick={confirmVars} disabled={selected.size < 2}>Confirm variables →</button>
              <span className="hint" style={{ marginLeft: 8 }}>{selected.size} selected (need ≥2)</span>
            </div>
          </div>
        ) : (
          <div style={{ padding: 16 }} className="note">{job?.prune_result?.offered.length} offered, confirmed.</div>
        )}
      </div>

      {/* STEP 3 */}
      <div className="card" style={{ opacity: s3done ? 1 : 0.5 }}>
        <StepHeader n={3} title="Feature importance" sub="multivariate importance + VIF · derived permutation threshold · prune loop"
          done={s4done} active={s3done && !s4done} />
        {!s3done ? (
          <div className="locked" style={{ padding: 16, color: "var(--ink-faint)", fontStyle: "italic" }}>Confirm variables first.</div>
        ) : job?.status === "running" ? (
          <div style={{ padding: 16 }}><BuildLog log={job.log} /></div>
        ) : (
          <div style={{ padding: 16 }}>
            <div className="note" style={{ marginBottom: 8 }}>
              Threshold <FieldHelp term="permutation_null" /><span className="mono">{job!.prune_result!.min_importance.toExponential(2)}</span> (500-permutation, family-wise 95th percentile of this panel's own noise)
            </div>
            <table className="stocks-table">
              <thead><tr><th className="l">Feature</th><th>Keep</th><th className="r">Importance</th><th className="r">VIF <FieldHelp term="vif" /></th><th>Reason if dropped</th></tr></thead>
              <tbody>
                {job!.prune_result!.offered.map((key) => {
                  const isSelected = job!.prune_result!.selected.includes(key);
                  const imp = job!.prune_result!.importance[key];
                  const vif = job!.prune_result!.vif[key];
                  const drop = job!.prune_result!.drops[key];
                  return (
                    <tr key={key}>
                      <td className="l mono">{key}</td>
                      <td>
                        <input type="checkbox" checked={selected.has(key) && isSelected}
                          disabled={!isSelected}
                          onChange={() => toggleKeepSelected(key)} />
                      </td>
                      <td className="r mono">{imp !== null && imp !== undefined ? imp.toExponential(2) : "—"}</td>
                      <td className="r mono" style={{ color: vif && vif > 10 ? "var(--bad)" : undefined }}>{vif !== null && vif !== undefined && Number.isFinite(vif) ? vif.toFixed(1) : "—"}</td>
                      <td style={{ fontSize: "0.76rem", color: "var(--ink-soft)" }}>{drop ?? ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!s4done && (
              <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
                <button className="btn ghost sm" onClick={rerunPrune}>↻ Re-run after pruning</button>
                <button className="btn primary sm" onClick={confirmImportance}>Lock feature set →</button>
                <span className="hint">untick to prune, then re-run</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* STEP 4 */}
      <div className="card" style={{ opacity: s4done ? 1 : 0.5 }}>
        <StepHeader n={4} title="Model bake-off" sub="≥4 families, same features, at the locked horizon, real walk-forward"
          done={s5done} active={s4done && !s5done} badge={s5done ? chosenFamily ?? undefined : undefined} />
        {!s4done ? (
          <div className="locked" style={{ padding: 16, color: "var(--ink-faint)", fontStyle: "italic" }}>Lock the feature set first.</div>
        ) : job?.status === "running" && job.stage === "bakeoff" ? (
          <div style={{ padding: 16 }}><BuildLog log={job.log} /></div>
        ) : (
          <div style={{ padding: 16 }}>
            <BakeoffTable result={job!.bakeoff_result!} chosen={chosenFamily} onChoose={setChosenFamily} />
            <div className="hint" style={{ marginTop: 10 }}>
              Same {job!.prune_result!.selected.length} features, at {String(job!.spec.horizon)}d, real embargoed{" "}
              <FieldHelp term="walk_forward" />8-fold walk-forward. Best on {job!.bakeoff_result!.prediction_type === "classification" ? "hit-rate" : "rank-IC"} highlighted — override if you prefer a simpler/stabler family.
            </div>
          </div>
        )}
      </div>

      {/* STEP 5 */}
      <div className="card" style={{ opacity: s4done ? 1 : 0.5 }}>
        <StepHeader n={5} title="Accept & persist" sub="serialize the winner to .pkl with full provenance" done={s5done} active={s4done && !s5done} />
        {!s4done ? (
          <div className="locked" style={{ padding: 16, color: "var(--ink-faint)", fontStyle: "italic" }}>Run the bake-off first.</div>
        ) : job?.status === "running" && job.stage === "persisting" ? (
          <div style={{ padding: 16 }}><BuildLog log={job.log} /></div>
        ) : s5done ? (
          <div style={{ padding: 16 }}>
            <ProvenanceBox job={job!} />
          </div>
        ) : refused && job?.stage !== "horizon_refused" ? (
          <div style={{ padding: 16 }}>
            <div className="banner show" style={{ display: "flex", background: "var(--down-soft)" }}>
              <span>⛔</span>
              <span><b>REFUSED at persist.</b> {job?.log[job.log.length - 1]?.message.replace("REFUSED: ", "")}</span>
            </div>
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input className="txt" style={{ minWidth: 280 }} value={modelId} onChange={(e) => setModelId(e.target.value)} placeholder="model_id" />
              <button className="btn go" onClick={acceptAndPersist} disabled={!modelId}>✓ Accept &amp; persist .pkl</button>
            </div>
            <div className="hint" style={{ marginTop: 8 }}>
              On accept: estimator → .pkl; target, horizon, feature contract, bake-off, and training window written to
              the registry. The new model starts un-championed — champion is earned via live scoring (H1), never set
              at build time.
            </div>
          </div>
        )}
        {jobError && <div className="error" style={{ margin: "0 16px 16px" }}>{job?.error}</div>}
      </div>
    </>
  );
}

function BuildLog({ log, collapsedByDefault }: { log: { ts: number; message: string }[]; collapsedByDefault?: boolean }) {
  const [open, setOpen] = useState(!collapsedByDefault);
  return (
    <div>
      {collapsedByDefault && (
        <button className="btn ghost sm" onClick={() => setOpen((o) => !o)}>{open ? "hide" : "show"} build log ({log.length})</button>
      )}
      {open && (
        <div className="mono" style={{ fontSize: "0.76rem", maxHeight: 220, overflow: "auto", background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 8, padding: "8px 10px", marginTop: 6 }}>
          {log.map((l, i) => <div key={i}>{l.message}</div>)}
        </div>
      )}
    </div>
  );
}

function HorizonCurve({ result }: { result: NonNullable<BuildJobDto["horizon_result"]> }) {
  const entries = Object.entries(result.curve).filter(([, v]) => v !== null) as [string, number][];
  const max = Math.max(...entries.map(([, v]) => v));
  return (
    <div style={{ marginTop: 10 }}>
      <div className="note" style={{ marginBottom: 6 }}>
        Performance vs horizon <FieldHelp term="horizon_shape" />
        <span className={`chip ${result.shape === "PLATEAU" ? "st-champion" : "st-challenger"}`} style={{ marginLeft: 8 }}>{result.shape}</span>
      </div>
      <table className="stocks-table">
        <thead><tr><th>Horizon</th><th className="r">OOS rank-IC</th><th>Robustness</th></tr></thead>
        <tbody>
          {entries.map(([h, v]) => (
            <tr key={h} style={{ background: Number(h) === result.locked ? "var(--ok-soft)" : undefined }}>
              <td className="mono">{h}d{Number(h) === result.locked ? " ◆ proposed" : ""}</td>
              <td className="r mono">{v >= 0 ? "+" : ""}{v.toFixed(4)}</td>
              <td>
                <div style={{ height: 8, borderRadius: 4, background: "var(--surface-2)", width: 100 }}>
                  <div style={{ height: "100%", width: `${Math.max(0, (v / max) * 100)}%`, background: "var(--accent)", borderRadius: 4 }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="hint" style={{ marginTop: 6 }}>{result.reason}</div>
    </div>
  );
}

function BakeoffTable({ result, chosen, onChoose }: {
  result: NonNullable<BuildJobDto["bakeoff_result"]>; chosen: string | null; onChoose: (f: string) => void;
}) {
  const rows = Object.entries(result.families).sort((a, b) => b[1].mean_score - a[1].mean_score);
  const metricLabel = result.prediction_type === "classification" ? "Hit-rate" : "rank-IC";
  return (
    <table className="stocks-table">
      <thead><tr><th className="l">Family</th><th className="r">{metricLabel}</th><th className="r">Folds</th><th></th></tr></thead>
      <tbody>
        {rows.map(([name, r]) => (
          <tr key={name} className={r.is_winner ? "best" : undefined} style={r.is_winner ? { background: "var(--ok-soft)" } : undefined}>
            <td className="l mono" style={{ fontWeight: 600 }}>{name}{r.is_winner && <span className="chip st-champion" style={{ marginLeft: 6 }}>best</span>}</td>
            <td className="r mono">{r.mean_score >= 0 ? "+" : ""}{r.mean_score.toFixed(4)}</td>
            <td className="r mono">{r.n_folds}</td>
            <td className="r">
              <label style={{ cursor: "pointer", fontSize: "0.78rem" }}>
                <input type="radio" name="winner" checked={chosen === name} onChange={() => onChoose(name)} /> keep
              </label>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProvenanceBox({ job }: { job: BuildJobDto }) {
  const p = job.persist_result!;
  return (
    <div className="prov">
      <div style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--rule)", fontSize: "0.84rem" }}>
        <span style={{ width: 160, flex: "none", fontFamily: "var(--font-display)", fontWeight: 600 }}>Model</span>
        <span className="mono">{p.model_id} v{p.version}</span>
      </div>
      <div style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--rule)", fontSize: "0.84rem" }}>
        <span style={{ width: 160, flex: "none", fontFamily: "var(--font-display)", fontWeight: 600 }}>Slot</span>
        <span className="mono">{p.slot_key}</span>
      </div>
      <div style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--rule)", fontSize: "0.84rem" }}>
        <span style={{ width: 160, flex: "none", fontFamily: "var(--font-display)", fontWeight: 600 }}>Horizon</span>
        <span className="mono">{p.horizon}d</span>
      </div>
      <div style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--rule)", fontSize: "0.84rem" }}>
        <span style={{ width: 160, flex: "none", fontFamily: "var(--font-display)", fontWeight: 600 }}>Family (winner)</span>
        <span className="mono">{p.family}</span>
      </div>
      <div style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--rule)", fontSize: "0.84rem" }}>
        <span style={{ width: 160, flex: "none", fontFamily: "var(--font-display)", fontWeight: 600 }}>Features ({p.n_features})</span>
        <span className="mono">{p.features.join(", ")}</span>
      </div>
      <div style={{ display: "flex", gap: 10, padding: "7px 0", fontSize: "0.84rem" }}>
        <span style={{ width: 160, flex: "none", fontFamily: "var(--font-display)", fontWeight: 600 }}>Artifact</span>
        <span className="pkl mono" style={{ color: "var(--ok)", background: "var(--ok-soft)", border: "1px solid var(--rule)", borderRadius: 6, padding: "3px 9px" }}>✓ {p.pkl_path}</span>
      </div>
      <div className="hint" style={{ marginTop: 10 }}>
        Persisted un-championed, enabled for daily scoring. It becomes champion only once it beats the slot's current
        contender on live evidence (Evaluation's promotion rule) — never at build time.
      </div>
    </div>
  );
}
